import { ErrorBoundary } from '@genial/components';
import { Color } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { Center } from 'native-base';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useRecoilQuery } from '../../../../../hooks/useRecoilQuery';
import { tradingListDetailsQuery } from '../../../../../store/trading/selector';
import { ObterIntradayResponseNormalized } from '../../../../../store/trading/types';

import { ContentDesktop } from './Desktop';
import { ContentMobile } from './Mobile';
import { ContentTablet } from './Tablet';

export interface IntradayContainerProps {
  accountId: number;
  sinacor: number;
  accountPNP: number;
  accountBP: number;
}

export interface IntradayContainerContentProps {
  tradingList?: ObterIntradayResponseNormalized[];
  tradingListLoading?: boolean;
  accountId: number;
  sinacor: number;
  accountPNP: number;
}

export const IntradayContainer: React.FC<IntradayContainerProps> = ({
  accountId,
  sinacor,
  accountPNP,
  accountBP,
}) => {
  const {
    data,
    error,
    refresher: tradingListRefresher,
    loading: tradingListLoading,
  } = useRecoilQuery(
    tradingListDetailsQuery({
      idConta: accountId,
      sinacor,
      idContaSuperPNP: accountPNP ? accountPNP : accountBP,
    }),
    {
      cachePolicy: 'no-cache',
    }
  );

  const [firstLoad, setFirstLoad] = useState(true);
  const [hydrateData, setHydrateData] = useState<ObterIntradayResponseNormalized[]>([]);
  const [errorCache, setErrorCache] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const refreshData = useCallback(() => {
    tradingListRefresher();
    timeoutRef.current = setTimeout(refreshData, 3000);
  }, [tradingListRefresher]);

  useEffect(() => {
    if (!!error && !errorCache && !tradingListLoading) {
      setErrorCache(true);
    }
    if (!error && !!data?.data && errorCache && !tradingListLoading) {
      setErrorCache(false);
    }

    if (data?.data && firstLoad) {
      setHydrateData(error ? [] : data?.data);
      setFirstLoad(false);
    }

    if (!firstLoad) {
      if (data?.data.length) {
        setHydrateData(error ? [] : data?.data);
      }
      timeoutRef.current = setTimeout(refreshData, 3000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, firstLoad, tradingListRefresher, refreshData, error, errorCache, tradingListLoading]);

  const contentProps = {
    tradingList: hydrateData,
    tradingListLoading: firstLoad,
    accountId,
    sinacor,
    accountPNP: accountPNP ? accountPNP : accountBP,
  };

  const content = useBreakpointValue({
    base: <ContentMobile {...contentProps} />,
    sm: <ContentTablet {...contentProps} />,
    md: <ContentDesktop {...contentProps} />,
  });

  if (errorCache) {
    return (
      <Center flex={1} backgroundColor={Color['neutral.white']}>
        <ErrorBoundary onRefresh={tradingListRefresher} />
      </Center>
    );
  }

  return !errorCache ? content : null;
};
